import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { StyleColorPalette } from './StyleColorPalette';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import { FOREST_GREEN } from '@/styles/tokens';
import { Save, Power, Plus, X, Pencil, Eye, Share2, Lock, Users, Globe, GitFork } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type {
  IStyleProfile, ExtractedColorPalette, ExtractedTypography,
  ExtractedSpacing, ExtractedEffects, StyleProfileVisibility,
} from '@/types';

interface StyleProfileEditorProps {
  profile: IStyleProfile;
  onSave: (id: string, updates: Partial<IStyleProfile>) => Promise<void>;
  onActivate: (id: string) => Promise<void>;
  projectId?: string;
  hasOrgWideBaseline?: boolean;
  canActivate?: boolean;
  canEdit?: boolean;
  canShare?: boolean;
}

type PaletteColorKey = keyof Pick<
  ExtractedColorPalette,
  | 'primary'
  | 'primaryLight'
  | 'primaryDark'
  | 'secondary'
  | 'accent'
  | 'background'
  | 'cardBackground'
  | 'headerBackground'
  | 'textPrimary'
  | 'textSecondary'
  | 'textMuted'
  | 'border'
>;

type SpacingTokenKey = keyof Pick<
  ExtractedSpacing,
  'gridUnit' | 'cardPadding' | 'sectionGap' | 'borderRadius'
>;

interface StableTextItem {
  localId: string;
  value: string;
}

interface StableDisplayItem<T> {
  localId: string;
  value: T;
}

const COLOR_KEYS = [
  'primary', 'primaryLight', 'primaryDark', 'secondary', 'accent',
  'background', 'cardBackground', 'headerBackground',
  'textPrimary', 'textSecondary', 'textMuted', 'border',
] satisfies readonly PaletteColorKey[];

const COLOR_LABELS: Record<PaletteColorKey, string> = {
  primary: 'Primary', primaryLight: 'Primary Light', primaryDark: 'Primary Dark',
  secondary: 'Secondary', accent: 'Accent', background: 'Background',
  cardBackground: 'Card Background', headerBackground: 'Header Background',
  textPrimary: 'Text Primary', textSecondary: 'Text Secondary',
  textMuted: 'Text Muted', border: 'Border',
};

const SPACING_TOKEN_DEFINITIONS = [
  { label: 'Grid Unit', key: 'gridUnit' },
  { label: 'Card Padding', key: 'cardPadding' },
  { label: 'Section Gap', key: 'sectionGap' },
  { label: 'Border Radius', key: 'borderRadius' },
] satisfies readonly { label: string; key: SpacingTokenKey }[];

const SHARE_VISIBILITY_OPTIONS = [
  { value: 'private', icon: Lock, label: 'Private', desc: 'Only your organization' },
  { value: 'shared', icon: Users, label: 'Shared', desc: 'Selected organizations' },
  { value: 'public', icon: Globe, label: 'Public', desc: 'Anyone on the platform' },
] satisfies readonly {
  value: StyleProfileVisibility;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  desc: string;
}[];

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function createStableTextItem(prefix: string, sequence: React.MutableRefObject<number>, value: string): StableTextItem {
  const localId = `${prefix}:${sequence.current}`;
  sequence.current += 1;
  return { localId, value };
}

function createStableTextItems(
  prefix: string,
  sequence: React.MutableRefObject<number>,
  values: readonly string[],
): StableTextItem[] {
  return values.map(value => createStableTextItem(prefix, sequence, value));
}

function createStableDisplayItems<T>(
  prefix: string,
  values: readonly T[],
  getSignature: (value: T) => string,
): StableDisplayItem<T>[] {
  const seen = new Map<string, number>();

  return values.map(value => {
    const signature = getSignature(value);
    const duplicateCount = seen.get(signature) ?? 0;
    seen.set(signature, duplicateCount + 1);
    return {
      localId: duplicateCount === 0 ? `${prefix}:${signature}` : `${prefix}:${signature}:${duplicateCount}`,
      value,
    };
  });
}

function hexToLuminance(hex: string): number | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return null;
  const r = parseInt(m[1].slice(0, 2), 16) / 255;
  const g = parseInt(m[1].slice(2, 4), 16) / 255;
  const b = parseInt(m[1].slice(4, 6), 16) / 255;
  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

function getContrastRatio(fg: string, bg: string): number | null {
  const l1 = hexToLuminance(fg);
  const l2 = hexToLuminance(bg);
  if (l1 === null || l2 === null) return null;
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

const CONTRAST_PAIRS: { fgKey: keyof ExtractedColorPalette; bgKey: keyof ExtractedColorPalette; label: string }[] = [
  { fgKey: 'textPrimary', bgKey: 'background', label: 'Text on Background' },
  { fgKey: 'textPrimary', bgKey: 'cardBackground', label: 'Text on Card' },
  { fgKey: 'textSecondary', bgKey: 'background', label: 'Secondary on Background' },
  { fgKey: 'textMuted', bgKey: 'cardBackground', label: 'Muted on Card' },
];

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <h4 className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground whitespace-nowrap">
        {children}
      </h4>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

export function StyleProfileEditor({
  profile,
  onSave,
  onActivate,
  projectId,
  hasOrgWideBaseline = false,
  canActivate = true,
  canEdit = true,
  canShare = true,
}: StyleProfileEditorProps) {
  const shadowSequence = useRef(0);
  const customRuleSequence = useRef(0);
  const [colors, setColors] = useState<ExtractedColorPalette>({ ...profile.colors });
  const [typography, setTypography] = useState<ExtractedTypography>({ ...profile.typography });
  const [spacing, setSpacing] = useState<ExtractedSpacing>({ ...profile.spacing });
  const [effects, setEffects] = useState<ExtractedEffects>({ ...profile.effects });
  const [shadowItems, setShadowItems] = useState<StableTextItem[]>(() =>
    createStableTextItems(`${profile.profileId}:shadow`, shadowSequence, profile.effects.shadows ?? []),
  );
  const [customRuleItems, setCustomRuleItems] = useState<StableTextItem[]>(() =>
    createStableTextItems(`${profile.profileId}:rule`, customRuleSequence, profile.customRules ?? []),
  );
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Sharing state
  const [shareVisibility, setShareVisibility] = useState<StyleProfileVisibility>(profile.visibility || 'private');
  const [shareOrgIds, setShareOrgIds] = useState<string[]>(profile.sharedWithOrgIds || []);
  const [shareTags, setShareTags] = useState(profile.tags?.join(', ') || '');
  const [shareDescription, setShareDescription] = useState(profile.description || '');
  const [shareOpen, setShareOpen] = useState(false);

  const userOrgs = useOrganizationStore(s => s.organizations);
  const otherOrgs = useMemo(() => userOrgs.filter(o => o._id !== profile.organizationId), [userOrgs, profile.organizationId]);
  const customRules = useMemo(() => customRuleItems.map(item => item.value), [customRuleItems]);
  const effectsForSave = useMemo(() => {
    const shadowValues = shadowItems.map(item => item.value);
    return effects.shadows !== undefined || shadowValues.length > 0
      ? { ...effects, shadows: shadowValues }
      : effects;
  }, [effects, shadowItems]);
  const activationDescription = useMemo(() => {
    if (!projectId) {
      return 'This will activate the profile for your organization. It will replace the current org-wide style profile, while project-specific active profiles remain unchanged.';
    }

    return hasOrgWideBaseline
      ? 'This will activate the profile for this project. It will replace the current project style profile. Your org-wide baseline will remain active.'
      : 'This will activate the profile for this project. It will replace the current project style profile.';
  }, [projectId, hasOrgWideBaseline]);
  const activeProjectIds = profile.activeProjectIds ?? [];
  const isOrgWideActiveBaseline = profile.status === 'active' && activeProjectIds.length === 0;
  const isActiveForCurrentScope = projectId
    ? activeProjectIds.includes(projectId)
    : isOrgWideActiveBaseline;
  const canActivateCurrentScope = canActivate
    && !isActiveForCurrentScope
    && !(projectId && isOrgWideActiveBaseline);

  useEffect(() => {
    if (!canEdit && isEditing) setIsEditing(false);
    if (!canShare && shareOpen) setShareOpen(false);
  }, [canEdit, canShare, isEditing, shareOpen]);

  const handleSave = useCallback(async () => {
    if (!canEdit) return;

    setIsSaving(true);
    try {
      await onSave(profile.profileId, { colors, typography, spacing, effects: effectsForSave, customRules });
      toast.success('Profile saved');
    } catch {
      toast.error('Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  }, [canEdit, profile.profileId, colors, typography, spacing, effectsForSave, customRules, onSave]);

  const handleActivate = useCallback(async () => {
    setShowActivateConfirm(false);
    try {
      await onActivate(profile.profileId);
      toast.success('Profile activated — AI will now use these design tokens');
    } catch {
      toast.error('Failed to activate profile');
    }
  }, [profile.profileId, onActivate]);

  const handleShareSave = useCallback(async () => {
    if (!canShare) return;

    const tags = shareTags.split(',').map(t => t.trim()).filter(Boolean);
    try {
      await onSave(profile.profileId, {
        visibility: shareVisibility,
        sharedWithOrgIds: shareVisibility === 'shared' ? shareOrgIds : [],
        tags,
        description: shareDescription,
      });
      toast.success('Sharing settings updated');
      setShareOpen(false);
    } catch {
      toast.error('Failed to update sharing settings');
    }
  }, [canShare, profile.profileId, shareVisibility, shareOrgIds, shareTags, shareDescription, onSave]);

  const populatedKeys = useMemo(
    () => COLOR_KEYS.filter(k => isNonEmptyString(colors[k])),
    [colors],
  );
  const colorEntries = useMemo(() => populatedKeys.flatMap(k => {
    const value = colors[k];
    if (!isNonEmptyString(value)) return [];
    return [{
      label: COLOR_LABELS[k],
      value,
    }];
  }), [populatedKeys, colors]);

  const handleColorChange = useCallback((index: number, value: string) => {
    setColors(prev => ({ ...prev, [populatedKeys[index]]: value }));
  }, [populatedKeys]);

  const heroBg = colors.background || colors.headerBackground || '#0f172a';
  const heroText = colors.textPrimary || '#e6e6e6';
  const heroSubtext = colors.textSecondary || colors.textMuted || '#94a3b8';
  const heroSwatchCandidates: Array<{ key: 'primary' | 'secondary' | 'accent'; color?: string }> = [
    { key: 'primary', color: colors.primary },
    { key: 'secondary', color: colors.secondary },
    { key: 'accent', color: colors.accent },
  ];
  const heroSwatches = heroSwatchCandidates.flatMap((swatch): Array<{ key: 'primary' | 'secondary' | 'accent'; color: string }> =>
    isNonEmptyString(swatch.color) ? [{ key: swatch.key, color: swatch.color }] : [],
  );

  const spacingTokens = SPACING_TOKEN_DEFINITIONS.flatMap(token => {
    const value = spacing[token.key];
    if (typeof value !== 'number') return [];
    return [{ ...token, value }];
  });
  const additionalColorItems = useMemo(
    () => createStableDisplayItems(
      'additional-color',
      colors.additional ?? [],
      c => `${c.name}:${c.value}:${c.usage ?? ''}`,
    ),
    [colors.additional],
  );
  const paddingScaleItems = useMemo(
    () => createStableDisplayItems('padding-scale', spacing.paddingScale ?? [], v => String(v)),
    [spacing.paddingScale],
  );
  const gapScaleItems = useMemo(
    () => createStableDisplayItems('gap-scale', spacing.gapScale ?? [], v => String(v)),
    [spacing.gapScale],
  );

  return (
    <div className="flex flex-col gap-0 -mx-1">
      {/* ─── Hero Banner ─────────────────────────────────────── */}
      <div
        className="relative px-5 pt-5 pb-6 rounded-lg mb-6"
        style={{ backgroundColor: heroBg }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {heroSwatches.length > 0 && (
              <div className="flex gap-1.5 mb-3">
                {heroSwatches.map(({ key, color }) => (
                  <div key={key} className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                ))}
              </div>
            )}
            <h3 className="text-xl font-bold tracking-tight truncate" style={{ color: heroText }}>
              {profile.name}
            </h3>
            <p className="text-xs mt-0.5 capitalize" style={{ color: heroSubtext }}>
              Source: {profile.source.type} · Status: {profile.status}
              {profile.visibility && profile.visibility !== 'private' && (
                <span className="ml-2 inline-flex items-center gap-1">
                  {profile.visibility === 'public' ? <Globe className="w-3 h-3 inline" /> : <Users className="w-3 h-3 inline" />}
                  {profile.visibility}
                </span>
              )}
            </p>
            {profile.forkedFrom && (
              <p className="text-[10px] mt-1 flex items-center gap-1 opacity-70" style={{ color: heroSubtext }}>
                <GitFork className="w-3 h-3" />
                Imported from {profile.forkedFrom.organizationName}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {isEditing ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
                >
                  <Save className="w-3.5 h-3.5" />
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsEditing(false)}
                  className="text-white/70 hover:text-white hover:bg-white/10"
                >
                  <Eye className="w-3.5 h-3.5" />
                  View Guide
                </Button>
              </>
            ) : (
              <>
                {canActivateCurrentScope && (
                  <Button
                    size="sm"
                    onClick={() => setShowActivateConfirm(true)}
                    style={{ backgroundColor: FOREST_GREEN.DEFAULT }}
                    className="text-white hover:opacity-90"
                  >
                    <Power className="w-3.5 h-3.5" />
                    Activate
                  </Button>
                )}
                {canShare && (
                  <Popover open={shareOpen} onOpenChange={setShareOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-white/70 hover:text-white hover:bg-white/10"
                      >
                        <Share2 className="w-3.5 h-3.5" />
                        Share
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 z-[10002]" align="end">
                      <div className="flex flex-col gap-3">
                        <p className="text-sm font-semibold">Share Settings</p>

                        {/* Visibility Radio */}
                        <div className="flex flex-col gap-1.5">
                          <p className="text-xs text-muted-foreground font-medium">Visibility</p>
                          {SHARE_VISIBILITY_OPTIONS.map(opt => (
                            <button
                              key={opt.value}
                              onClick={() => setShareVisibility(opt.value)}
                              className={cn(
                                'flex items-center gap-2.5 px-3 py-2 rounded-md text-left text-sm transition-colors',
                                shareVisibility === opt.value
                                  ? 'bg-primary/10 ring-1 ring-primary'
                                  : 'hover:bg-muted',
                              )}
                            >
                              <opt.icon className="w-4 h-4 shrink-0 text-muted-foreground" />
                              <div>
                                <p className="font-medium text-xs">{opt.label}</p>
                                <p className="text-[10px] text-muted-foreground">{opt.desc}</p>
                              </div>
                            </button>
                          ))}
                        </div>

                        {/* Org selector (only for 'shared') */}
                        {shareVisibility === 'shared' && otherOrgs.length > 0 && (
                          <div className="flex flex-col gap-1.5">
                            <p className="text-xs text-muted-foreground font-medium">Share with organizations</p>
                            <div className="max-h-32 overflow-y-auto flex flex-col gap-1 border rounded-md p-1.5">
                              {otherOrgs.map(org => (
                                <label key={org._id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted cursor-pointer text-xs">
                                  <input
                                    type="checkbox"
                                    checked={shareOrgIds.includes(org._id)}
                                    onChange={(e) => {
                                      setShareOrgIds(prev =>
                                        e.target.checked
                                          ? [...prev, org._id]
                                          : prev.filter(id => id !== org._id)
                                      );
                                    }}
                                    className="rounded"
                                  />
                                  {org.name}
                                </label>
                              ))}
                            </div>
                            {otherOrgs.length === 0 && (
                              <p className="text-[10px] text-muted-foreground italic">You are not a member of other organizations</p>
                            )}
                          </div>
                        )}

                        {/* Tags */}
                        <div className="flex flex-col gap-1">
                          <p className="text-xs text-muted-foreground font-medium">Tags</p>
                          <Input
                            value={shareTags}
                            onChange={(e) => setShareTags(e.target.value)}
                            placeholder="e.g., dark, minimal, corporate"
                            className="h-8 text-xs"
                          />
                        </div>

                        {/* Description */}
                        <div className="flex flex-col gap-1">
                          <p className="text-xs text-muted-foreground font-medium">Description</p>
                          <Textarea
                            value={shareDescription}
                            onChange={(e) => setShareDescription(e.target.value)}
                            placeholder="Brief description for discoverability..."
                            className="text-xs resize-none"
                            rows={2}
                          />
                        </div>

                        <Button size="sm" onClick={handleShareSave}>
                          Save Sharing Settings
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
                {canEdit && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsEditing(true)}
                    className="text-white/70 hover:text-white hover:bg-white/10"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Edit
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ─── Brand Guide Body ─────────────────────────────────── */}
      <div className="flex flex-col gap-7 px-1">

        {/* Color Palette */}
        <div>
          <SectionHeading>Color Palette</SectionHeading>
          <StyleColorPalette colors={colorEntries} editable={isEditing} onChange={handleColorChange} />

          {additionalColorItems.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-3">
              {additionalColorItems.map(({ localId, value: c }) => (
                <div key={localId} className="flex flex-col items-center gap-1">
                  <div
                    className="w-9 h-9 rounded-md border border-border"
                    style={{ backgroundColor: c.value }}
                    title={`${c.name}: ${c.value}${c.usage ? ` (${c.usage})` : ''}`}
                  />
                  <span className="text-[9px] text-muted-foreground truncate max-w-[44px] text-center">{c.name}</span>
                </div>
              ))}
            </div>
          )}

          {/* WCAG Contrast Table */}
          {CONTRAST_PAIRS.some(p => isNonEmptyString(colors[p.fgKey]) && isNonEmptyString(colors[p.bgKey])) && (
            <div className="mt-5">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                Contrast Ratios (WCAG 2.1)
              </p>
              <div className="rounded-lg border border-border overflow-hidden">
                {CONTRAST_PAIRS.map(({ fgKey, bgKey, label }, i) => {
                  const fg = colors[fgKey];
                  const bg = colors[bgKey];
                  if (!isNonEmptyString(fg) || !isNonEmptyString(bg)) return null;
                  const ratio = getContrastRatio(fg, bg);
                  if (ratio === null) return null;
                  const passAA = ratio >= 4.5;
                  const passAAA = ratio >= 7;
                  return (
                    <div
                      key={label}
                      className={cn('flex items-center gap-3 px-3 py-2 text-xs', i > 0 && 'border-t border-border')}
                    >
                      <div className="flex items-center gap-0.5 shrink-0">
                        <div className="w-4 h-4 rounded border border-border" style={{ backgroundColor: fg }} />
                        <span className="text-muted-foreground px-0.5">/</span>
                        <div className="w-4 h-4 rounded border border-border" style={{ backgroundColor: bg }} />
                      </div>
                      <span className="flex-1 text-muted-foreground truncate">{label}</span>
                      <span className="font-mono font-semibold">{ratio.toFixed(1)}:1</span>
                      <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-bold', passAA ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600')}>AA</span>
                      <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-bold', passAAA ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600')}>AAA</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Typography */}
        <div>
          <SectionHeading>Typography</SectionHeading>
          <div className="grid grid-cols-2 gap-3">
            {/* Heading specimen */}
            <div className="rounded-lg border border-border p-4 bg-card flex flex-col">
              <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Header Font</p>
              <div
                className="text-5xl font-bold leading-none mb-2 flex-1"
                style={{ fontFamily: typography.headingFamily || 'inherit' }}
              >
                Aa
              </div>
              <p className="text-[9px] text-muted-foreground leading-relaxed mb-3">
                A B C D E F G H I J K L M<br />
                N O P Q R S T U V W X Y Z<br />
                a b c d e f g h i j k l m<br />
                n o p q r s t u v w x y z<br />
                1 2 3 4 5 6 7 8 9 0
              </p>
              {isEditing ? (
                <Input
                  value={typography.headingFamily || ''}
                  onChange={(e) => setTypography(prev => ({ ...prev, headingFamily: e.target.value }))}
                  className="h-7 text-xs"
                  placeholder="e.g., Inter Bold, sans-serif"
                />
              ) : (
                <p className="text-xs font-medium">
                  {typography.headingFamily || <span className="text-muted-foreground italic">Not set</span>}
                </p>
              )}
            </div>

            {/* Body specimen */}
            <div className="rounded-lg border border-border p-4 bg-card flex flex-col">
              <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Body Type</p>
              <div
                className="text-5xl font-normal leading-none mb-2 flex-1"
                style={{ fontFamily: typography.bodyFamily || 'inherit' }}
              >
                Aa
              </div>
              <p className="text-[9px] text-muted-foreground leading-relaxed mb-3">
                A B C D E F G H I J K L M<br />
                N O P Q R S T U V W X Y Z<br />
                a b c d e f g h i j k l m<br />
                n o p q r s t u v w x y z<br />
                1 2 3 4 5 6 7 8 9 0
              </p>
              {isEditing ? (
                <Input
                  value={typography.bodyFamily || ''}
                  onChange={(e) => setTypography(prev => ({ ...prev, bodyFamily: e.target.value }))}
                  className="h-7 text-xs"
                  placeholder="e.g., Arial, sans-serif"
                />
              ) : (
                <p className="text-xs font-medium">
                  {typography.bodyFamily || <span className="text-muted-foreground italic">Not set</span>}
                </p>
              )}
            </div>
          </div>

          {/* Type Scale */}
          {typography.sizeScale && Object.keys(typography.sizeScale).length > 0 && (
            <div className="mt-4">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                Type Scale
              </p>
              <div className="flex items-end gap-4 flex-wrap overflow-x-auto pb-1">
                {Object.entries(typography.sizeScale)
                  .sort(([, a], [, b]) => a - b)
                  .map(([key, val]) => (
                    <div key={key} className="flex flex-col items-center gap-1 shrink-0">
                      <span
                        className="font-medium leading-none"
                        style={{
                          fontSize: `${Math.min(val, 40)}px`,
                          fontFamily: typography.headingFamily || 'inherit',
                        }}
                      >
                        Ag
                      </span>
                      <span className="text-[9px] text-muted-foreground">{val}px</span>
                      <span className="text-[9px] text-muted-foreground/60">{key}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Spacing & Layout */}
        {spacingTokens.length > 0 && (
          <div>
            <SectionHeading>Spacing & Layout</SectionHeading>
            <div className="flex flex-wrap gap-3">
              {spacingTokens.map(token => (
                <div key={token.key} className="flex flex-col items-center gap-1">
                  {isEditing ? (
                    <>
                      <Input
                        type="number"
                        value={token.value}
                        onChange={(e) => setSpacing(prev => ({ ...prev, [token.key]: Number(e.target.value) }))}
                        className="h-10 w-20 text-center text-sm font-mono"
                        min={0}
                      />
                      <span className="text-[10px] text-muted-foreground text-center">{token.label}</span>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-center w-16 h-16 rounded-lg border border-border bg-muted/40 text-sm font-mono font-semibold">
                        {token.value}px
                      </div>
                      <span className="text-[10px] text-muted-foreground text-center max-w-[64px] leading-tight">
                        {token.label}
                      </span>
                    </>
                  )}
                </div>
              ))}
            </div>

            {(spacing.paddingScale?.length || spacing.gapScale?.length) ? (
              <div className="mt-3 flex flex-col gap-2">
                {spacing.paddingScale && spacing.paddingScale.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground w-14">Padding</span>
                    {paddingScaleItems.map(({ localId, value }) => (
                      <span key={localId} className="px-2 py-0.5 text-[10px] bg-muted rounded font-mono">{value}px</span>
                    ))}
                  </div>
                )}
                {spacing.gapScale && spacing.gapScale.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground w-14">Gap</span>
                    {gapScaleItems.map(({ localId, value }) => (
                      <span key={localId} className="px-2 py-0.5 text-[10px] bg-muted rounded font-mono">{value}px</span>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}

        {/* Effects */}
        {(shadowItems.length || effects.borderStyle) && (
          <div>
            <SectionHeading>Effects</SectionHeading>
            <div className="flex flex-col gap-3">
              {shadowItems.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">Shadows</p>
                  {isEditing ? (
                    <div className="flex flex-col gap-1.5">
                      {shadowItems.map((item) => (
                        <Input
                          key={item.localId}
                          value={item.value}
                          onChange={(e) => {
                            setShadowItems(prev => prev.map(current =>
                              current.localId === item.localId ? { ...current, value: e.target.value } : current
                            ));
                          }}
                          className="h-8 text-xs font-mono"
                          placeholder="0 2px 4px rgba(0,0,0,0.1)"
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="flex gap-3 flex-wrap">
                      {shadowItems.map((item) => (
                        <div
                          key={item.localId}
                          className="w-16 h-12 rounded-lg bg-card border border-border"
                          style={{ boxShadow: item.value }}
                          title={item.value}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
              {effects.borderStyle && (
                <div className="flex items-center gap-3">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground w-24 shrink-0">Border Style</p>
                  {isEditing ? (
                    <Input
                      value={effects.borderStyle}
                      onChange={(e) => setEffects(prev => ({ ...prev, borderStyle: e.target.value }))}
                      className="h-8 text-xs font-mono flex-1"
                    />
                  ) : (
                    <span className="text-xs font-mono bg-muted px-2 py-1 rounded">{effects.borderStyle}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Usage Guidelines */}
        {(customRules.length > 0 || isEditing) && (
          <div>
            <SectionHeading>Usage Guidelines</SectionHeading>
            {isEditing ? (
              <div className="flex flex-col gap-2">
                {customRuleItems.map((item) => (
                  <div key={item.localId} className="flex items-center gap-2">
                    <Input
                      value={item.value}
                      onChange={(e) => {
                        setCustomRuleItems(prev => prev.map(current =>
                          current.localId === item.localId ? { ...current, value: e.target.value } : current
                        ));
                      }}
                      className="h-8 text-sm flex-1"
                      placeholder="e.g., Always use rounded corners on cards"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => setCustomRuleItems(prev => prev.filter(current => current.localId !== item.localId))}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCustomRuleItems(prev => [
                    ...prev,
                    createStableTextItem(`${profile.profileId}:rule`, customRuleSequence, ''),
                  ])}
                >
                  <Plus className="w-3.5 h-3.5" /> Add Rule
                </Button>
              </div>
            ) : (
              <ul className="space-y-2">
                {customRuleItems.map((item) => (
                  <li key={item.localId} className="flex items-start gap-2.5 text-sm">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                    {item.value}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

      </div>

      {/* Activate Confirmation */}
      <AlertDialog open={showActivateConfirm} onOpenChange={setShowActivateConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Activate Style Profile?</AlertDialogTitle>
            <AlertDialogDescription>
              {activationDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleActivate}>Activate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
